/**
 * Copyright © 2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

/**
 * Shared helpers for surfacing backend error messages to the user.
 *
 * The backend's global exception middleware (ExceptionHandlingMiddleware) returns a JSON body
 * shaped as `{ message: "..." }` for expected, user-actionable failures - e.g. "Zamp API token is
 * not configured. Please add your API token in Zamp Settings." (thrown as a
 * UserActionRequiredException; see ZampQBOService.Constants.ERROR_ZAMP_API_TOKEN_NOT_CONFIGURED
 * on the backend). Every fetch call that can hit one of those failures should surface that exact
 * message to the user instead of a generic fallback, so they know what to fix.
 */

/**
 * Safely parses a Response body as JSON, returning `fallback` (default: null) instead of
 * throwing if the body is empty or not valid JSON. Reads the body as text first so this never
 * throws on an empty response (e.g. a 204, or a body already consumed elsewhere).
 */
export const safeJson = async (res, fallback = null) => {
    const text = await res.text();
    if (!text) return fallback;
    try {
        return JSON.parse(text);
    } catch {
        return fallback;
    }
};

/**
 * Extracts a user-facing error message from a failed fetch Response. Prefers the backend's own
 * `{ message }` body; falls back to `fallbackMessage` when the body is missing, unparsable, or
 * has no message field (e.g. a plain 500 or an unrelated ProblemDetails shape).
 */
export const extractErrorMessage = async (res, fallbackMessage) => {
    const body = await safeJson(res);
    return body?.message || fallbackMessage;
};
